import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import VerticalAlignTopOutlinedIcon from '@mui/icons-material/VerticalAlignTopOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { api } from '../../../app/http-common';
import {
  getLanguageName, getArticlePublisherName,
} from '../../CreateBrief/transformers';
import { PBNEW_STORY, STATUS_LABELS, STATUS_TEXT_COLORS, formatShortDateTime } from '../constants';
import useFlipMenu from '../hooks/useFlipMenu';
import { canReorderPast, rowOrderZone } from '../reorderGroups';

// A story row in Bucket Stories (Corrections spec 6.5 + Delta §1.2 + §1.3).
// - No assignee — that lives in In Production.
// - Whole non-control area is the drag surface (§1.3).
// - Clicking that same non-control area toggles a read-only source review
//   directly beneath the row (§1.2). Never contains write controls.
export default function StoryRow({
  item,
  index,
  checked,
  onToggleCheck,
  onAdvanceStatus,
  onMoveTo,
  onMoveToTop,
  onCopyTo,
  onArchive,
  onUnarchive,
  onDelete,
  onReject,
  onOpenComment,
  onOpenFullStory,
  onCopyStoryLink,
  onReorder,
  onReorderCommit,
  // Follow-up §7 — expansion is CONTROLLED by the parent so it can be captured
  // into the return context and reopened after Item Details. Keeping it local
  // meant Back always came back to a collapsed row.
  expanded = false,
  onToggleExpanded,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sources, setSources] = useState(null);      // null = not fetched
  const [loadingSources, setLoadingSources] = useState(false);
  const [sourcesError, setSourcesError] = useState(null);
  const menuRef = useRef(null);
  // §12 — keep the overflow menu inside the viewport for rows near the bottom.
  const { wrapRef: flipWrapRef, menuRef: flipMenuRef, style: flipStyle } = useFlipMenu(menuOpen);
  const story = item.story || {};
  // A NULL `item.story` means the GroupSubject lookup missed entirely — the row
  // points at a story this database does not have. That is different from a
  // story that exists but has no title, and the two used to render the same
  // opaque `Story 4e7707`, which reads like a titling bug rather than a missing
  // referent.
  //
  // It is the normal state on staging: syncBucketsFromProd mirrors production's
  // publisherbucketstories, but staging runs its own indexer against its own
  // database, so the two environments' GroupSubject ids are disjoint sets and
  // every synced storyId dangles. In production it means the story was deleted
  // and the row should be archived.
  const storyMissing = !item.story;
  const title = storyMissing
    ? 'Story unavailable'
    : (story.title || `Story ${item.storyId?.toString().slice(-6) || ''}`);
  const articleCount = story.articleCount ?? item.articleCountSnapshot ?? 0;
  const when = item.statusChangedAt || item.createdAt;
  const isSuggestion = item.status === 'suggestion';
  const hasComment = !!item.clientFeedbackComment;

  const rowRef = useRef(null);
  const dragStartedRef = useRef(false);

  const [{ isDragging }, dragRef, dragPreviewRef] = useDrag(
    () => ({
      type: PBNEW_STORY,
      item: {
        bucketStoryId: item._id,
        storyId: item.storyId,
        status: item.status,
        // Carried so the drop target can reason about zones at all. Without it
        // the target could only compare statuses, accepted every cross-zone
        // drop, and left the parent to discard it in silence.
        orderZone: rowOrderZone(item),
        title: story.title || 'story',
        index,
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      end: (_dragged, monitor) => {
        // Persist only when the drop was inside the list (a reorder). A drop on
        // a target bucket is a Move/Copy and is handled by that panel.
        if (!monitor.didDrop()) onReorderCommit?.();
      },
    }),
    [item._id, item.status, item.orderZone, story.title, index, onReorderCommit],
  );

  // Reorder is available within the same status (§6.3 unchanged). Crossing an
  // orderZone is allowed and re-zones the row, see reorderGroups.js.
  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: PBNEW_STORY,
      canDrop: (dragged) => canReorderPast(dragged, item) && dragged.bucketStoryId !== item._id,
      hover: (dragged) => {
        if (!canReorderPast(dragged, item)) return;
        if (dragged.index === index || dragged.index == null) return;
        // Advance the drag's own index ONLY if the parent actually applied the
        // step. Doing it unconditionally meant one refused step desynchronised
        // the drag, and the next legal step moved a different story.
        if (onReorder?.(dragged.index, index)) {
          dragged.index = index;
          dragged.orderZone = rowOrderZone(item);
        }
      },
      collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
    }),
    [item._id, item.status, index, onReorder],
  );

  // The drag source is `.pbnew-story-main`, which spans the handle AND the body.
  //
  // It used to be the body alone, with the dotted handle sitting OUTSIDE it as
  // a decorative sibling, carrying `cursor: grab` and a "Drag to a target
  // bucket" tooltip while being the one part of the row that could not start a
  // drag. Grabbing the dots did nothing, and grabbing the body instead competes
  // with its click-to-expand, so a drag that fell short expanded the story.
  //
  // dropRef stays on the outer row so hover-swap still lights up the whole row,
  // and the drag preview is the whole row rather than just the header strip.
  dropRef(rowRef);
  dragPreviewRef(rowRef);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  // §12 — "If a status change relocates a row while a row-anchored selector is
  // open, close or reposition that selector." A status change re-groups the row,
  // so the anchor moves out from under the menu; close it rather than leave it
  // floating at a stale position.
  useEffect(() => { setMenuOpen(false); }, [item.status]);

  const run = (fn) => { setMenuOpen(false); fn(item); };

  // §1.2 — first time the row expands, fetch its sources.
  const loadSources = useCallback(async () => {
    if (sources !== null || loadingSources) return;
    setLoadingSources(true);
    setSourcesError(null);
    try {
      const res = await api.articlesByStory(item.storyId);
      // The endpoint returns an array (see http-common.js). Normalise to the
      // handful of fields the review needs so the render stays trivial.
      const list = Array.isArray(res) ? res : (res?.articles || []);
      setSources(list.map((a) => ({
        _id: a._id,
        headline: a.subject || a.title || '(untitled)',
        url: a.url || null,
        publisher: getArticlePublisherName(a),
        language: getLanguageName(a.lang) || (a.lang || null),
      })));
    } catch (err) {
      setSourcesError(err?.response?.data?.error || 'Could not load source articles.');
    } finally {
      setLoadingSources(false);
    }
  }, [item.storyId, sources, loadingSources]);

  const toggleExpanded = useCallback(() => {
    if (!expanded) loadSources();
    onToggleExpanded?.(item._id);
  }, [expanded, loadSources, onToggleExpanded, item._id]);

  // Fetch sources when the parent restores this row as already-expanded
  // (§7.3 — Back reopens the inline review, which must repopulate).
  useEffect(() => {
    if (expanded) loadSources();
  }, [expanded, loadSources]);

  // §1.3 — click on the body toggles expand; click on a control does that
  // control's action. Controls stop propagation with their own onClick, so we
  // just need to distinguish "was the click inside a control" here.
  const onBodyClick = useCallback((e) => {
    // A drag consumed this click — do not also treat it as a toggle.
    if (dragStartedRef.current) { dragStartedRef.current = false; return; }
    const isControl = e.target.closest(
      'button, a, input, select, .pbnew-story-actions, .pbnew-comment-btn',
    );
    if (isControl) return;
    toggleExpanded();
  }, [toggleExpanded]);

  return (
    <div
      ref={rowRef}
      // §7.3 — lets the parent find this row to re-anchor the viewport on return.
      data-bucket-story-id={item._id}
      className={`pbnew-story ${isDragging ? 'dragging' : ''} ${isOver ? 'reorder-over' : ''} ${expanded ? 'expanded' : ''}`}
    >
      <div
        className="pbnew-story-main"
        ref={dragRef}
        // Must sit on the drag source itself: `dragstart` fires here and
        // bubbles upward, so a handler on the body below would never run and
        // every completed drag would also be treated as a click-to-expand.
        onDragStart={() => { dragStartedRef.current = true; }}
      >
        <input
          type="checkbox"
          className="pbnew-checkbox"
          style={{ marginTop: 2 }}
          checked={checked}
          onChange={(e) => onToggleCheck(item._id, e)}
          onClick={(e) => e.stopPropagation()}
        />

        {/* The handle is inside the drag source now, so grabbing the dots works. */}
        <span className="pbnew-story-drag" title="Drag to reorder, or onto a target bucket">
          <DragIndicatorOutlinedIcon style={{ fontSize: 16 }} />
        </span>

        {/* Follow-up §7.3 — a visible disclosure control. Clicking the row body
            already toggled the inline review, but nothing SHOWED that, so the
            behaviour was undiscoverable (§12.1 step 6 hit the same wall). The
            chevron points right when closed and down when open, is itself
            clickable, and carries an accessible label that changes with state. */}
        <button
          type="button"
          className={`pbnew-story-disclosure ${expanded ? 'open' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleExpanded(); }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse story sources' : 'Expand story sources'}
          title={expanded ? 'Collapse story sources' : 'Expand story sources'}
        >
          {expanded
            ? <ExpandMoreOutlinedIcon style={{ fontSize: 18 }} />
            : <ChevronRightOutlinedIcon style={{ fontSize: 18 }} />}
        </button>

        <div
          className="pbnew-story-body"
          onClick={onBodyClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(); }
          }}
        >
          <div className="pbnew-story-title">
            {item.latestListNumber != null && (
              <span className="pbnew-story-number">{item.latestListNumber}.</span>
            )}
            <span className={storyMissing ? 'pbnew-story-missing' : undefined}>{title}</span>
            {storyMissing && (
              <span
                className="pbnew-missing-tag"
                title={`This bucket row references story ${item.storyId}, which is not in this database. On staging that is expected — bucket rows are mirrored from production, whose stories live in a separate database. In production it means the story was deleted.`}
              >
                not in this database
              </span>
            )}
          </div>
          <div className="pbnew-story-meta">
            {articleCount} article{articleCount === 1 ? '' : 's'} ·{' '}
            <button
              type="button"
              className="pbnew-status-btn"
              style={{ color: STATUS_TEXT_COLORS[item.status] || 'inherit' }}
              onClick={(e) => { e.stopPropagation(); onAdvanceStatus(item); }}
              title="Advance status"
            >
              {STATUS_LABELS[item.status] || item.status}
            </button>
            {when ? ` · ${formatShortDateTime(when)}` : ''}
            {item.isArchived && <span className="pbnew-archived-tag">Archived</span>}
            {hasComment && (
              <button
                type="button"
                className="pbnew-comment-btn"
                onClick={(e) => { e.stopPropagation(); onOpenComment(item); }}
                title="Client comment"
              >
                <ChatBubbleOutlineOutlinedIcon style={{ fontSize: 13 }} />
              </button>
            )}
          </div>
        </div>

        <div
          className="pbnew-story-actions"
          style={{ position: 'relative' }}
          ref={(el) => { menuRef.current = el; flipWrapRef.current = el; }}
        >
          <button className="pbnew-icon-btn" title="More" onClick={() => setMenuOpen((v) => !v)}>
            <MoreHorizOutlinedIcon style={{ fontSize: 18 }} />
          </button>
          {/* Spec 6.7 — the row × means Delete. */}
          <button className="pbnew-icon-btn" title="Delete" onClick={() => onDelete(item)}>
            <CloseOutlinedIcon style={{ fontSize: 16 }} />
          </button>
          {menuOpen && (
            <div className="pbnew-menu" ref={flipMenuRef} style={{ right: 0, ...flipStyle }}>
              {item.isArchived ? (
                <button className="pbnew-menu-item" onClick={() => run(onUnarchive)}>
                  <UnarchiveOutlinedIcon style={{ fontSize: 16 }} /> Unarchive
                </button>
              ) : (
                <button className="pbnew-menu-item" onClick={() => run(onArchive)}>
                  <ArchiveOutlinedIcon style={{ fontSize: 16 }} /> Archive
                </button>
              )}
              <button className="pbnew-menu-item" onClick={() => run(onDelete)}>
                <DeleteOutlineOutlinedIcon style={{ fontSize: 16 }} /> Delete
              </button>
              <button className="pbnew-menu-item danger" onClick={() => run(onReject)}>
                <BlockOutlinedIcon style={{ fontSize: 16 }} /> Reject
              </button>
              {/* §A2 — active Suggestions only; other statuses stay reorderable
                  within their own block but do not get this action. */}
              {isSuggestion && (
                <>
                  <div className="pbnew-menu-sep" />
                  <button className="pbnew-menu-item" onClick={() => run(onMoveToTop)}>
                    <VerticalAlignTopOutlinedIcon style={{ fontSize: 16 }} /> Move to top
                  </button>
                </>
              )}
              <div className="pbnew-menu-sep" />
              {/* Adi #2 — copying a story's title + Alchemiq link existed on the
                  old screen and in In Production's row menu, but was never
                  carried into this one. Same action, same wording. */}
              <button className="pbnew-menu-item" onClick={() => run(onCopyStoryLink)}>
                <LinkOutlinedIcon style={{ fontSize: 16 }} /> Copy story title + link
              </button>
              <button className="pbnew-menu-item" onClick={() => run(onCopyTo)}>
                <ContentCopyOutlinedIcon style={{ fontSize: 16 }} /> Copy to…
              </button>
              {isSuggestion && (
                <button className="pbnew-menu-item" onClick={() => run(onMoveTo)}>
                  <DriveFileMoveOutlinedIcon style={{ fontSize: 16 }} /> Move to…
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* §1.2 — inline read-only source review. Deliberately carries no
          checkboxes, status controls, Archive/Delete/Reject, Copy or Move. */}
      {expanded && (
        <div className="pbnew-story-sources">
          {loadingSources && <div className="pbnew-story-sources-hint">Loading source articles…</div>}
          {sourcesError && <div className="pbnew-story-sources-hint error">{sourcesError}</div>}
          {sources && sources.length === 0 && (
            <div className="pbnew-story-sources-hint">No source articles for this story.</div>
          )}
          {sources && sources.length > 0 && (
            <ul className="pbnew-story-sources-list">
              {sources.map((src) => (
                <li key={src._id}>
                  {src.url ? (
                    <a href={src.url} target="_blank" rel="noopener noreferrer">
                      {src.headline}
                      <OpenInNewOutlinedIcon style={{ fontSize: 12, marginLeft: 4, verticalAlign: 'middle' }} />
                    </a>
                  ) : (
                    <span>{src.headline}</span>
                  )}
                  <span className="meta">
                    {src.publisher || 'Unknown source'}
                    {src.language ? ` · ${src.language}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="pbnew-story-sources-foot">
            <button
              type="button"
              className="pbnew-linkish"
              disabled={!story.slug}
              onClick={() => story.slug && onOpenFullStory?.(item)}
            >
              Open full story
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
